import { LitElement, html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { InputStyleController } from '@/controllers/InputStyleController';
import { EVENTS } from '../constants';
import throttle from 'lodash/throttle';
import { ref, createRef } from 'lit/directives/ref.js';

export default class SotbitSelectStyle extends LitElement {
    static properties = {
        name: { type: String, state: true },
        placeholder: { type: String },
        size: { type: String },
        label: { type: String },
        disabled: { type: Boolean, state: true },
        readonly: { type: Boolean, state: true },
        multiple: { type: Boolean, state: true },
        isOpen: { type: Boolean, state: true },
        defaultIcon: { type: String, attribute: 'default-icon' },
        helperText: { type: String, attribute: 'helper-text' },
        errorText: { type: String, attribute: 'error-text' },
        error: { type: Boolean },
        clearable: { type: Boolean },
        _displayValue: { type: String, state: true },
        _hasSelectSlot: { type: Boolean, state: true },
        _options: { type: Array, state: true },
    };

    constructor() {
        super();

        this.label = null;
        this.placeholder = null;
        this.isOpen = false;
        this.defaultIcon = '';
        this.clearable = false;
        this._handleOutsideClick = this._handleOutsideClick.bind(this);
        this._displayValue = this.placeholder;
        this._options = [];
        this._portalNode = null;
        this._slotChangeThrottled = null;
        this.selectRef = createRef();

        this._controller = new InputStyleController(this, {
            getInput: () => {
                return this.select;
            },
        });
        this._controller.inputObserverConfig.attributeFilter = [
            ...this._controller.inputObserverConfig.attributeFilter,
            'multiple',
        ];
    }

    connectedCallback() {
        super.connectedCallback();

        this.name = this.name || null;
        this.disabled = this.disabled || false;
        this.readonly = this.readonly || false;
        this.multiple = this.multiple || false;

        document.addEventListener(EVENTS.CLICK, this._handleOutsideClick);

        if (this.select) {
            this._controller?.inputObserver?.observe(this.select, this._controller.inputObserverConfig);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._controller?.inputObserver?.disconnect();
        document.removeEventListener(EVENTS.CLICK, this._handleOutsideClick);
    }

    _handleSelectSlotChange({ currentTarget }) {
        if (!this._slotChangeThrottled) {
            this._slotChangeThrottled = throttle((currentTarget) => {
                const inputSlotElements = currentTarget.assignedElements({
                    flatten: true,
                });
                this.select = inputSlotElements[0];

                this._controller?.inputObserver?.disconnect();
                if (this.select) {
                    this.select.removeEventListener('blur', this._handleBlur);
                    this.select.addEventListener('blur', this._handleBlur);
                    this._controller?.initHandlers();
                    this._controller?.inputObserver.observe(this.select, this._controller?.inputObserverConfig);
                    this._controller?.calcInputAttr();
                }

                for (const child of this.children) {
                    if (child?.tagName?.toUpperCase() === 'SELECT' || child?.tagName?.toUpperCase() === 'OPTION') {
                        continue;
                    }

                    child?.remove();
                }

                this._options = Array.from(this.querySelectorAll('option'));

                if (this.clearable) {
                    this._options = this._options.filter((option) => option?.value);
                }
            }, 50);
        }

        this._slotChangeThrottled(currentTarget);
    }

    _delegateSelect() {
        const select = this.querySelector('select');

        if (select) {
            select.dispatchEvent(
                new Event('change', {
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    _handleOutsideClick(event) {
        if (
            this.isOpen &&
            !event.composedPath().includes(this) &&
            this._portalNode &&
            !event.composedPath().includes(this._portalNode)
        ) {
            this.hide();
        }
    }

    getOptionsTemplate() {
        return html`${map(
            this._options,
            (option) => html`
                <sotbit-select-option
                    ?selected="${option.selected}"
                    ?multiple="${this.multiple}"
                    size="${this.size}"
                    value="${option.value}"
                    label="${option.textContent}"
                >
                    <span>${option.textContent}</span>
                </sotbit-select-option>
            `
        )}`;
    }

    show() {
        this.selectRef?.value?.show();
    }

    hide() {
        this.selectRef?.value?.hide();
    }

    _syncOption(originalOption, styleOption) {
        if (!originalOption || !styleOption) {
            return;
        }

        originalOption.selected = styleOption.selected;
        originalOption.toggleAttribute('selected', styleOption.selected);
    }

    _handleChange() {
        const options = this.selectRef?.value?.options;

        options?.forEach((option, optionIndex) => {
            if (this._options[optionIndex]) {
                this._syncOption(this._options[optionIndex], option);
            }
        });

        this._delegateSelect();
    }

    render() {
        return html`
            <div style="display: none;">
                <slot name="select" @slotchange=${this._handleSelectSlotChange}></slot>
            </div>
            <sotbit-select
                ${ref(this.selectRef)}
                @change="${this._handleChange}"
                size="${this.size || nothing}"
                label="${this.label || nothing}"
                helper-text="${this.helperText || nothing}"
                error-text="${this.errorText || nothing}"
                ?multiple="${this.multiple}"
                ?required="${this.required}"
                ?readonly="${this.readonly}"
                ?disabled="${this.disabled}"
                ?clearable="${this.clearable}"
                ?error="${this.error}"
            >
                ${this.getOptionsTemplate()}
            </sotbit-select>
        `;
    }
}
